/**
 * Модалка создания / редактирования папки чатов.
 * Поля: название + сетка иконок (по категориям) + чаты с чекбоксами.
 * Если editFolder задан — режим редактирования.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createChatFolder, editChatFolder, addTopicToFolder, removeTopicFromFolder, userAvatarUrl, topicAvatarUrl,
  fetchUsersByDepartment,
  type ChatTopic, type ChatFolder,
} from "@/api/dashboard";
import { Icon } from "@/components/prototype/Icon";
import { FOLDER_ICONS, type FolderIconCategory, parseFolderIcon } from "@/data/folderIcons";

const AVATAR_PALETTE = ["#4fae4e","#3390ec","#9c7ce5","#00acc1","#e85c5c","#e99a12"];
function colorForKey(key: string): string {
  const hash = (key || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}
function initialsFor(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const CATEGORY_LABELS: Record<FolderIconCategory, string> = {
  main:    "Основные",
  people:  "Люди",
  work:    "Работа",
  places:  "Места",
  objects: "Объекты",
  fun:     "Разное",
};

export function NewFolderModal({ topics, onClose, editFolder }: {
  topics: ChatTopic[];
  onClose: () => void;
  editFolder?: ChatFolder | null;
}) {
  const isEdit = !!editFolder;
  const qc = useQueryClient();

  // Initial state — derive from editFolder если редактируем
  const initialIconId = useMemo(() => {
    if (editFolder?.icon) {
      const parsed = parseFolderIcon(editFolder.icon);
      if (parsed.kind === "svg") return parsed.def.id;
    }
    return "folder";
  }, [editFolder]);

  const [name, setName] = useState(editFolder?.name || "");
  const [iconId, setIconId] = useState<string>(initialIconId);
  const [selected, setSelected] = useState<Set<number>>(
    new Set(editFolder?.topic_ids || [])
  );

  // Если editFolder меняется — сбросим state
  useEffect(() => {
    setName(editFolder?.name || "");
    setIconId(initialIconId);
    setSelected(new Set(editFolder?.topic_ids || []));
  }, [editFolder, initialIconId]);

  const iconsByCategory = useMemo(() => {
    const map = new Map<FolderIconCategory, typeof FOLDER_ICONS>();
    for (const ic of FOLDER_ICONS) {
      const arr = map.get(ic.category) || [];
      arr.push(ic);
      map.set(ic.category, arr);
    }
    return map;
  }, []);

  const submitMut = useMutation({
    mutationFn: async () => {
      if (isEdit && editFolder) {
        await editChatFolder(editFolder.id, { name: name.trim(), icon: `svg:${iconId}` });
        // Diff topics: удалить убранные, добавить новые
        const wasSet = new Set(editFolder.topic_ids);
        for (const tid of selected) if (!wasSet.has(tid)) {
          try { await addTopicToFolder(editFolder.id, tid); } catch (e) { console.error(e); }
        }
        for (const tid of editFolder.topic_ids) if (!selected.has(tid)) {
          try { await removeTopicFromFolder(editFolder.id, tid); } catch (e) { console.error(e); }
        }
        return editFolder;
      } else {
        const folder = await createChatFolder({ name: name.trim(), icon: `svg:${iconId}` });
        for (const tid of selected) {
          try { await addTopicToFolder(folder.id, tid); } catch (e) { console.error(e); }
        }
        return folder;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat/folders"] });
      onClose();
    },
    onError: (e: Error) => alert(`Не удалось ${isEdit ? "сохранить" : "создать"}: ${e.message}`),
  });

  function toggleTopic(id: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function submit() { if (!name.trim()) return; submitMut.mutate(); }

  return (
    <div className="mng-modal-overlay show" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mng-modal" onClick={(e) => e.stopPropagation()}>
        <header className="mng-modal-header">
          <span className="mng-modal-title">{isEdit ? "Редактировать папку" : "Новая папка"}</span>
          <button className="mng-modal-close" onClick={onClose} title="Закрыть">
            <Icon name="x" size={16}/>
          </button>
        </header>
        <div className="mng-modal-body">
          <div className="mng-modal-group">
            <label className="mng-modal-label">Название папки</label>
            <input
              type="text"
              className="mng-modal-input"
              placeholder="Например: Работа"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="mng-modal-group">
            <label className="mng-modal-label">Иконка</label>
            <div className="mng-folder-icon-categories">
              {Array.from(iconsByCategory.entries()).map(([cat, icons]) => (
                <div key={cat} className="mng-folder-icon-cat">
                  <div className="mng-folder-icon-cat-label">{CATEGORY_LABELS[cat]}</div>
                  <div className="mng-folder-icons">
                    {icons.map(ic => (
                      <button
                        key={ic.id}
                        type="button"
                        className={`mng-folder-icon-opt${iconId === ic.id ? " active" : ""}`}
                        onClick={() => setIconId(ic.id)}
                        title={ic.name}
                      >
                        <svg viewBox="0 0 24 24" width="22" height="22">
                          <path fill="currentColor" d={ic.path}/>
                        </svg>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mng-modal-group">
            <label className="mng-modal-label">Быстро добавить из отдела</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {([
                { key: "sales", label: "Отдел продаж" },
                { key: "production", label: "Производство" },
                { key: "marketing", label: "Маркетинг" },
                { key: "admin", label: "Администрация" },
                { key: "finance", label: "Финансы" },
                { key: "supply", label: "Отдел снабжения" },
              ] as const).map(d => (
                <button
                  key={d.key}
                  type="button"
                  className={`dept-tag dept-tag-${d.key}`}
                  style={{ cursor: "pointer", border: "none" }}
                  onClick={async () => {
                    try {
                      const users = await fetchUsersByDepartment(d.key);
                      const userIds = new Set(users.map(u => u.id));
                      // Топики: (а) с этикеткой этого отдела, (б) DM с юзерами отдела
                      const matchedTopicIds = new Set<number>();
                      for (const t of topics) {
                        if ((t as { department?: string | null }).department === d.key) matchedTopicIds.add(t.id);
                        if (t.kind === "dm" && t.dm_partner && userIds.has(t.dm_partner.user_id)) matchedTopicIds.add(t.id);
                      }
                      if (matchedTopicIds.size === 0) {
                        alert(`В "${d.label}" нет подходящих чатов`);
                        return;
                      }
                      setSelected(prev => {
                        const next = new Set(prev);
                        for (const id of matchedTopicIds) next.add(id);
                        return next;
                      });
                    } catch (e) {
                      alert((e as Error).message);
                    }
                  }}
                  title={`Добавить все чаты из «${d.label}»`}
                >
                  + {d.label}
                </button>
              ))}
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: "rgba(0,0,0,.45)" }}>
              Можно убрать конкретные чаты галочкой в списке ниже.
            </div>
          </div>

          <div className="mng-modal-group">
            <label className="mng-modal-label">{isEdit ? "Чаты в папке" : "Добавить чаты в папку"}</label>
            <div className="mng-user-list">
              {topics.length === 0 && <div className="mng-modal-empty">Нет доступных чатов</div>}
              {topics.map(t => {
                const isSel = selected.has(t.id);
                const display = t.kind === "dm" && t.dm_partner ? (t.dm_partner.display_name || t.name) : t.name;
                const avaUrl = t.kind === "dm" && t.dm_partner
                  ? userAvatarUrl(t.dm_partner.user_id, t.dm_partner.avatar_filename)
                  : (t.avatar_filename ? topicAvatarUrl(t.id, t.avatar_filename) : null);
                const avaKey = t.kind === "dm" && t.dm_partner ? t.dm_partner.user_id : `t-${t.id}`;
                return (
                  <div
                    key={t.id}
                    className={`mng-user-list-item${isSel ? " selected" : ""}`}
                    onClick={() => toggleTopic(t.id)}
                  >
                    {avaUrl ? (
                      <img className="mng-avatar" src={avaUrl} alt=""/>
                    ) : (() => {
                      const ic = t.icon || "";
                      if (ic.startsWith("pack:")) {
                        const n = parseInt(ic.slice(5), 10);
                        if (Number.isFinite(n) && n >= 1 && n <= 254) {
                          return <img className="mng-avatar" src={`/icons/icon-${n}.png`} alt=""
                            style={{ objectFit: "contain", background: "transparent" }}/>;
                        }
                      }
                      const isEmoji = ic && !ic.startsWith("svg:") && !ic.startsWith("lottie") && !ic.startsWith("http");
                      return (
                        <div className="mng-avatar" style={{ background: colorForKey(avaKey), fontSize: isEmoji ? 22 : undefined }}>
                          {isEmoji ? ic : initialsFor(display)}
                        </div>
                      );
                    })()}
                    <div className="mng-list-info">
                      <div className="mng-list-name">{display}</div>
                      <div className="mng-list-status">{t.kind === "dm" ? "Личный чат" : "Группа"}</div>
                    </div>
                    <div className="mng-checkbox"><Icon name="check" size={14}/></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mng-modal-footer">
          <button className="mng-btn mng-btn-secondary" onClick={onClose}>Отмена</button>
          <button
            className="mng-btn mng-btn-primary"
            disabled={!name.trim() || submitMut.isPending}
            onClick={submit}
          >
            {submitMut.isPending ? (isEdit ? "Сохранение…" : "Создание…") : (isEdit ? "Сохранить" : "Создать")}
          </button>
        </div>
      </div>
    </div>
  );
}
